import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ProductFeatures {
  showBilling: boolean;
  showTerms: boolean;
  updated_at?: string | null;
}

/**
 * Runtime product switches from GET/PUT /api/features (PostgreSQL app_feature_flags).
 *
 * Security model (fail closed):
 * - Until GET succeeds with explicit true, both switches are OFF.
 * - On login / session refresh, flags are cleared to OFF then re-fetched.
 * - PUT is Irish Taylor staff admins only (backend enforces + writes audit feature_flag_set).
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.apiBaseUrl;

  private readonly billing = signal(false);
  private readonly terms = signal(false);
  private readonly ready = signal(false);
  private readonly failure = signal<string | null>(null);
  private readonly saving = signal(false);

  private inFlight: Observable<ProductFeatures> | null = null;

  readonly showBilling = this.billing.asReadonly();
  readonly showTerms = this.terms.asReadonly();
  readonly loaded = this.ready.asReadonly();
  readonly loadError = this.failure.asReadonly();
  readonly isSaving = this.saving.asReadonly();

  /** Strict === true — never treat missing/undefined as enabled. */
  readonly billingEnabled = computed(() => this.ready() && this.billing() === true);
  readonly termsEnabled = computed(() => this.ready() && this.terms() === true);

  /**
   * Fetch current switches from the server.
   * @param force When true (login / refresh), wipe local ON flags first so a
   *              tampered or stale client cannot keep features open.
   */
  load(force = false): Observable<ProductFeatures> {
    if (force) {
      this.clearToClosed();
      this.inFlight = null;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.http
      .get<ProductFeatures>(`${this.api}/features`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      })
      .pipe(
        map((response) => normalize(response)),
        tap((features) => this.apply(features)),
        catchError((error: unknown) => {
          this.clearToClosed(true);
          this.failure.set(describe(error));
          return of({ showBilling: false, showTerms: false });
        }),
        finalize(() => {
          this.inFlight = null;
        }),
        shareReplay(1)
      );

    return this.inFlight;
  }

  /**
   * Persist one or both switches. Backend rejects non–staff-admins and audits the change.
   * Response values from the server replace local state (never trust the request).
   */
  update(patch: { showBilling?: boolean; showTerms?: boolean }): Observable<ProductFeatures> {
    this.saving.set(true);
    this.failure.set(null);
    return this.http.put<ProductFeatures>(`${this.api}/features`, patch).pipe(
      map((response) => normalize(response)),
      tap((features) => this.apply(features)),
      catchError((error: unknown) => {
        this.failure.set(describe(error));
        // Re-sync from server so a failed write cannot leave a lying UI.
        this.load(true).subscribe();
        return throwError(() => error);
      }),
      finalize(() => this.saving.set(false))
    );
  }

  private apply(features: ProductFeatures): void {
    this.billing.set(features.showBilling === true);
    this.terms.set(features.showTerms === true);
    this.ready.set(true);
    this.failure.set(null);
  }

  /** Fail closed — features stay off until a successful load. */
  private clearToClosed(markReady = false): void {
    this.billing.set(false);
    this.terms.set(false);
    this.ready.set(markReady);
  }
}

function normalize(response: Partial<ProductFeatures> | null | undefined): ProductFeatures {
  return {
    showBilling: response?.showBilling === true,
    showTerms: response?.showTerms === true,
    updated_at: response?.updated_at ?? null
  };
}

function describe(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  const status = (error as { status?: number } | null)?.status;
  return status
    ? `Could not load feature switches (HTTP ${status}).`
    : 'Could not load feature switches.';
}
